import React, { useState, useEffect, useContext } from "react";
import { SelectedAlertsContext } from "../../../utils/SelectedAlertContext";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from "@mui/material";
import { styled } from "@mui/system";
import CloseIcon from '@mui/icons-material/Close';
import { format, parse } from "date-fns";
import deleteIcon from '../../../assets/images/deleteIcon.svg'
import ActionPopup from "../../../Samarth Module/components/popup/actionPopup";
import CustomButton from "../../components/buttons/CustomButton";
import styles from "./insight/insight.module.css";


const StyledTableCell = styled(TableCell)(({ theme }) => ({
    "&.MuiTableCell-head": {
        fontFamily: "Unilever Shilling",
        backgroundColor: "#2036C7",
        color: "#fff",
        textAlign: "left",
        padding: "5px",
        fontSize: "12px",
        fontWeight: 600,
        border: "1px solid #EAECF0",
    },
    "&.MuiTableCell-body": {
        fontFamily: "Unilever Shilling",
        fontSize: 12,
        fontWeight: 400,
        padding: "5px",
        color: "#2B2B2B",
        border: "1px solid #EAECF0",
    },
}))

const StyledTableRow = styled(TableRow)({
    backgroundColor: "#FFFFFF !important"
});

const ReviewCart = ({ onClose, type, onAccept }) => {
    const { selections, setSelections } = useContext(SelectedAlertsContext);
    const [data, setData] = useState([]);
    const [openPopup, setOpenPopup] = useState(false);

    const alertTypeKey = type === 'RMPM Alert' ? 'rmpm' : 'agileOr';
    const acceptedAlerts = selections[alertTypeKey]?.accepted || [];

    const columns = type === "Agile OR"
        ? [
            { label: "Alert Id", key: "Alert Id" },
            { label: "Site cluster", key: "Site cluster" },
            { label: "Site code", key: "Site code" },
            { label: "CBU code", key: "CBU code" },
            { label: "CBU description", key: "CBU description" },
            { label: "Qty. risk", key: "Qty. risk" },
            { label: "Quantity at risk recovered", key: "Quantity at risk recovered" },
            { label: "Recoverability", key: "Recoverability" },
            { label: "Final recommendation", key: "Intelligent recommendation" },
        ]
        : [
            { label: "Alert Id", key: "Alert ID" },
            { label: "Site location", key: "Site cluster" },
            { label: "Site Code", key: "Site code" },
            { label: "Material Description", key: "Material description" },
            { label: "Qty at risk", key: "Qty at risk" },
            { label: "Quantity at risk recovered", key: "Quantity at risk recovered" },
            { label: "Final recommendation", key: "Supplying Site" },
            { label: "Delivery date", key: "Recommendation date" }
        ];

    useEffect(() => {
        if (acceptedAlerts.length > 0) {
            const formattedData = acceptedAlerts.flatMap(alert => {
                return (alert.selectedRecom || []).map(recom => {
                    const extractValue = (label) => {
                        const item = recom.data?.find(d => d.label === label);
                        if (!item) return null;
                        return label === 'Recommendation date'
                            ? item.value
                            : parseFloat(item.value).toFixed(1);
                    };

                    // Get the display title - use Info if Title is "Manual PO", otherwise use Title
                    const displayTitle = recom.Title === 'Manual PO' && recom.Info
                        ? recom.Info
                        : recom.Title;

                    return type === "RMPM Alert" ? {
                        alertId: alert["Alert ID"],
                        "Alert Id": alert["Alert ID"],
                        "Site location": alert["Site cluster"],
                        "Site Code": alert["Site code"],
                        "Material Description": alert["Material description"],
                        "CBU Description": alert["CBU description"],
                        "Qty at risk": alert["Qty at risk"],
                        "Base Unit of Measure": alert["Base Unit of Measure"],
                        "Quantity at risk recovered": extractValue("Quantity at risk recovered") || extractValue("Quantity At Risk"),
                        "Final recommendation": displayTitle,
                        "Original Title": recom.Title,
                        "Delivery date": extractValue("Recommendation date")
                    } : {
                        alertId: alert["Alert ID"],
                        "Alert Id": alert["Alert ID"],
                        "Site cluster": alert["Site cluster"],
                        "Site code": alert["Site code"],
                        "CBU code": alert["CBU code"],
                        "CBU description": alert["CBU description"],
                        "Qty. risk": `${alert["Projected Loss"]} T`,
                        "Quantity at risk recovered": extractValue("Recovered qty") ? `${extractValue("Recovered qty")} T` : 'null',
                        "Recoverability": extractValue("Recoverability") ? `${extractValue("Recoverability")} %` : "N/A",
                        "Final recommendation": recom.Title,
                        "Original Title": recom.Title,
                    };
                });
            });
            setData(formattedData);
        }
    }, [acceptedAlerts, type]);

    const removeFromCart = (alertId, originalTitle) => {
        setSelections(prev => {
            const updatedAccepted = prev[alertTypeKey]?.accepted?.map(alert => {
                if (alert["Alert ID"] === alertId) {
                    const filteredRecoms = (alert.selectedRecom || []).filter(
                        recom => recom.Title !== originalTitle
                    );
                    return { ...alert, selectedRecom: filteredRecoms };
                }
                return alert;
            }).filter(alert => (alert.selectedRecom || []).length > 0);

            return {
                ...prev,
                [alertTypeKey]: {
                    ...prev[alertTypeKey],
                    accepted: updatedAccepted
                }
            };
        });

        // Update local data and check if cart is empty
        setData(prev => {
            const updatedData = prev.filter(
                alert => !(alert["Alert Id"] === alertId && alert["Original Title"] === originalTitle)
            );

            // Close drawer if cart becomes empty
            if (updatedData.length === 0) {
                onClose();
            }

            return updatedData;
        });
    };

    const handleSubmit = () => {
        setOpenPopup(true);
        if (onAccept) {
            onAccept(acceptedAlerts.length);
        }
    };


    return (
        <>
            <Box className={styles.cartHeader}>
                <div className={styles.stickyTitle}>
                    <Typography className={styles.cartTitle} variant="body2">
                        Review cart
                    </Typography>
                    <CloseIcon className={styles.closeIcon} onClick={onClose} />
                </div>
            </Box>
            <TableContainer className={styles.table}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            {columns.map((col, index) => (
                                <StyledTableCell key={index}>
                                    {col.label}
                                </StyledTableCell>
                            ))}
                            <StyledTableCell>Delete record</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.map((alert, index) => (
                            <StyledTableRow key={`${alert["Alert Id"]}-${index}`}>
                                {columns.map((col, idx) => {
                                    let cellValue = alert[col.label];
                                    if (col.key === "Quantity at risk recovered") {
                                        cellValue = (alert[col.label] && alert[col.label] !== 'null') ? alert[col.label] : "N/A";
                                    }
                                    else if (col.key === "Delivery date") {
                                        cellValue = alert[col.label]
                                            ? format(new Date(alert[col.label]), "dd/MMM/yy, EEE")
                                            : "N/A";
                                    }
                                    return (
                                        <StyledTableCell key={idx}>
                                            {cellValue || "N/A"}
                                        </StyledTableCell>
                                    )
                                })}
                                <StyledTableCell>
                                    <img
                                        src={deleteIcon}
                                        alt="Delete Icon"
                                        className={styles.deleteIcon}
                                        onClick={() => removeFromCart(alert["Alert Id"], alert["Original Title"])}
                                    />
                                </StyledTableCell>
                            </StyledTableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer >
            <div className={styles.btnContainer}>
                <CustomButton type="reviewCartCancel" label="Cancel" onClick={onClose}>
                    Cancel
                </CustomButton>
                <CustomButton type="reviewCartSubmit" label="Submit" onClick={handleSubmit}>
                    Submit
                </CustomButton>
            </div>

            {/* Action Dialog */}
            <ActionPopup open={openPopup} onClose={() => setOpenPopup(false)} onConfirm={() => {
                setOpenPopup(false);
                onClose();
            }} type={type} />
        </>
    );
};

export default ReviewCart;

